/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Zalify has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * Filter product color swatches by shade group.
 *
 * Quick-buy drawers and popovers are fetched and inserted after page load.
 * Keeping this behavior in the globally loaded custom.js makes it available
 * to both the PDP and dynamically rendered quick-buy content.
 */
(() => {
  if (window.SKShadeTabs) {
    return;
  }

  window.SKShadeTabs = {
    init(root = document) {
      if (!root.querySelectorAll) {
        return;
      }

      root.querySelectorAll('[data-shade-tabs]').forEach((tabs) => {
        if (tabs.dataset.shadeTabsInitialized === 'true') {
          return;
        }

        tabs.dataset.shadeTabsInitialized = 'true';
        this.apply(tabs, 'all', false);
      });
    },

    apply(tabs, group, shouldChangeSelection) {
      const selector = tabs.closest('.product-form__option-selector');
      const list = selector ? selector.querySelector('[data-shade-swatch-list]') : null;

      if (!list) {
        return;
      }

      tabs.querySelectorAll('[data-shade-tab]').forEach((tab) => {
        const isActive = tab.dataset.shadeTab === group;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      if (shouldChangeSelection) {
        this.centerActiveTab(tabs, group);
      }

      list.querySelectorAll('.color-swatch').forEach((swatch) => {
        const swatchGroups = (swatch.dataset.shadeGroup || '')
          .trim()
          .split(/\s+/)
          .filter(Boolean);
        const isVisible = group === 'all' || swatchGroups.includes(group);
        swatch.classList.toggle('sk-shade-group-hidden', !isVisible);
      });

      if (!shouldChangeSelection || group === 'all') {
        return;
      }

      const checkedInput = list.querySelector('.color-swatch__radio:checked');
      const checkedSwatch = checkedInput ? checkedInput.closest('.color-swatch') : null;

      if (checkedSwatch && !checkedSwatch.classList.contains('sk-shade-group-hidden')) {
        return;
      }

      const nextInput = Array.from(list.querySelectorAll('.color-swatch__radio')).find((input) => {
        const swatch = input.closest('.color-swatch');

        return (
          swatch &&
          !input.disabled &&
          !swatch.hidden &&
          !swatch.classList.contains('is-disabled') &&
          !swatch.classList.contains('sk-shade-group-hidden')
        );
      });

      if (nextInput && !nextInput.checked) {
        nextInput.checked = true;
        nextInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    },

    centerActiveTab(tabs, group) {
      const escapedGroup = window.CSS && CSS.escape ? CSS.escape(group) : group.replace(/"/g, '\\"');
      const activeTab = tabs.querySelector(`[data-shade-tab="${escapedGroup}"]`);

      if (!activeTab) {
        return;
      }

      const targetLeft = activeTab.offsetLeft - (tabs.clientWidth - activeTab.offsetWidth) / 2;

      tabs.scrollTo({
        left: Math.max(targetLeft, 0),
        behavior: 'smooth',
      });
    },
  };

  document.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-shade-tab]');

    if (!tab) {
      return;
    }

    const tabs = tab.closest('[data-shade-tabs]');

    if (!tabs) {
      return;
    }

    window.SKShadeTabs.apply(tabs, tab.dataset.shadeTab, false);
    window.SKShadeTabs.centerActiveTab(tabs, tab.dataset.shadeTab);
  });

  document.addEventListener('shopify:section:load', (event) => {
    window.SKShadeTabs.init(event.target);
  });

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) {
          return;
        }

        if (node.matches('[data-shade-tabs]')) {
          window.SKShadeTabs.init(node.parentElement || document);
        } else if (node.querySelector('[data-shade-tabs]')) {
          window.SKShadeTabs.init(node);
        }
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  window.SKShadeTabs.init(document);
})();

/**
 * Deferred image loading for hidden interactive content (popovers, drawers, mega menus).
 * Images use data-deferred-src instead of src until the container opens or is hovered.
 */
(() => {
  if (window.SKDeferredImages) {
    return;
  }

  const OPENABLE_SELECTOR =
    'popover-content, modal-content, collapsible-content, drawer-content, mobile-navigation, cart-drawer';

  function isInsideNestedClosedOpenable(img, openedRoot) {
    let element = img.parentElement;

    while (element && element !== openedRoot) {
      if (element.matches?.(OPENABLE_SELECTOR) && !element.hasAttribute('open')) {
        return true;
      }

      element = element.parentElement;
    }

    return false;
  }

  function loadDeferredPictureSources(root) {
    if (!root || !root.querySelectorAll) {
      return;
    }

    root.querySelectorAll('source[data-deferred-srcset]:not([data-deferred-loaded])').forEach((source) => {
      const srcset = source.getAttribute('data-deferred-srcset');

      if (!srcset) {
        return;
      }

      source.srcset = srcset;
      source.setAttribute('data-deferred-loaded', 'true');
      source.removeAttribute('data-deferred-srcset');
    });
  }

  function loadDeferredImages(root) {
    if (!root || !root.querySelectorAll) {
      return;
    }

    loadDeferredPictureSources(root);

    root.querySelectorAll('img[data-deferred-src]:not([data-deferred-loaded])').forEach((img) => {
      if (isInsideNestedClosedOpenable(img, root)) {
        return;
      }

      const src = img.getAttribute('data-deferred-src');

      if (!src) {
        return;
      }

      img.src = src.startsWith('//') ? `${window.location.protocol}${src}` : src;

      const srcset = img.getAttribute('data-deferred-srcset');

      if (srcset) {
        img.srcset = srcset;
      }

      const sizes = img.getAttribute('data-deferred-sizes');

      if (sizes) {
        img.sizes = sizes;
      }

      img.setAttribute('data-deferred-loaded', 'true');
      img.removeAttribute('data-deferred-src');
      img.removeAttribute('data-deferred-srcset');
      img.removeAttribute('data-deferred-sizes');
    });
  }

  function loadMegaMenuImages(trigger) {
    const listItem = trigger.closest('li');

    if (!listItem) {
      return;
    }

    const megaMenu = listItem.querySelector('.mega-menu');

    if (!megaMenu || megaMenu.hasAttribute('data-deferred-images-loaded')) {
      return;
    }

    megaMenu.setAttribute('data-deferred-images-loaded', 'true');
    loadDeferredImages(megaMenu);
  }

  document.addEventListener('openable-element:open', (event) => {
    loadDeferredImages(event.target);
  });

  document.addEventListener(
    'mouseenter',
    (event) => {
      const trigger = event.target.closest('.header__linklist > li > a, .header__linklist > li > button');

      if (trigger) {
        loadMegaMenuImages(trigger);
      }
    },
    true,
  );

  document.addEventListener('focusin', (event) => {
    const trigger = event.target.closest('.header__linklist > li > a, .header__linklist > li > button');

    if (trigger) {
      loadMegaMenuImages(trigger);
    }
  });

  function patchSlideshowDeferredLoading() {
    const tagName = 'slide-show-item';

    const applyPatch = (Constructor) => {
      if (!Constructor || Constructor.prototype.__skDeferredPatched) {
        return;
      }

      const originalUntilReady = Constructor.prototype._untilReady;

      Constructor.prototype._untilReady = async function slideshowUntilReadyWithDeferred() {
        loadDeferredImages(this);
        return originalUntilReady.call(this);
      };

      Constructor.prototype.__skDeferredPatched = true;
    };

    const Constructor = customElements.get(tagName);

    if (Constructor) {
      applyPatch(Constructor);
    } else {
      customElements.whenDefined(tagName).then(() => applyPatch(customElements.get(tagName)));
    }
  }

  patchSlideshowDeferredLoading();

  document.addEventListener('shopify:section:load', () => {
    patchSlideshowDeferredLoading();
  });

  window.SKDeferredImages = {
    load: loadDeferredImages,
    loadSingle(img) {
      if (!img || !img.hasAttribute('data-deferred-src') || img.hasAttribute('data-deferred-loaded')) {
        return Promise.resolve();
      }

      const src = img.getAttribute('data-deferred-src');

      if (!src) {
        return Promise.resolve();
      }

      img.src = src.startsWith('//') ? `${window.location.protocol}${src}` : src;

      const srcset = img.getAttribute('data-deferred-srcset');

      if (srcset) {
        img.srcset = srcset;
      }

      const sizes = img.getAttribute('data-deferred-sizes');

      if (sizes) {
        img.sizes = sizes;
      }

      img.setAttribute('data-deferred-loaded', 'true');
      img.removeAttribute('data-deferred-src');
      img.removeAttribute('data-deferred-srcset');
      img.removeAttribute('data-deferred-sizes');

      if (img.complete && img.naturalWidth > 0) {
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      });
    },
  };
})();

/**
 * Load deferred product-item variant / secondary images before theme.js swatch switching.
 */
(() => {
  if (window.SKProductItemDeferredImages) {
    return;
  }

  function ensureDeferredImageLoaded(img) {
    if (window.SKDeferredImages?.loadSingle) {
      return window.SKDeferredImages.loadSingle(img);
    }

    return Promise.resolve();
  }

  function loadSecondaryHoverImage(img) {
    if (!img || !img.hasAttribute('data-hover-src') || img.hasAttribute('data-hover-loaded')) {
      return Promise.resolve();
    }

    const src = img.getAttribute('data-hover-src');

    if (!src) {
      return Promise.resolve();
    }

    img.src = src.startsWith('//') ? `${window.location.protocol}${src}` : src;

    const srcset = img.getAttribute('data-hover-srcset');

    if (srcset) {
      img.srcset = srcset;
    }

    const sizes = img.getAttribute('data-hover-sizes');

    if (sizes) {
      img.sizes = sizes;
    }

    img.setAttribute('data-hover-loaded', 'true');
    img.removeAttribute('data-hover-src');
    img.removeAttribute('data-hover-srcset');
    img.removeAttribute('data-hover-sizes');

    if (img.complete && img.naturalWidth > 0) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
    });
  }

  function bindProductItemSecondaryHover(root = document) {
    root.querySelectorAll('.product-item__image-wrapper--multiple').forEach((wrapper) => {
      if (wrapper.dataset.skSecondaryHoverBound === 'true') {
        return;
      }

      wrapper.dataset.skSecondaryHoverBound = 'true';

      wrapper.addEventListener(
        'pointerenter',
        () => {
          const img = wrapper.querySelector('img.product-item__secondary-image[data-hover-src]');

          if (img) {
            void loadSecondaryHoverImage(img);
          }
        },
        { passive: true },
      );
    });
  }

  function findSwatchVariantImage(input) {
    if (!input?.hasAttribute('data-variant-featured-media')) {
      return null;
    }

    const productItem = input.closest('product-item');

    if (!productItem) {
      return null;
    }

    const mediaId = input.getAttribute('data-variant-featured-media');

    return productItem.querySelector(
      `.product-item__primary-image[data-media-id="${CSS.escape(mediaId)}"]`,
    );
  }

  function loadSwatchVariantImage(input) {
    const newImage = findSwatchVariantImage(input);

    if (!newImage) {
      return Promise.resolve();
    }

    return ensureDeferredImageLoaded(newImage);
  }

  /**
   * theme.js binds _onColorSwatchChanged in connectedCallback before this file runs,
   * so prototype patching cannot intercept those handlers. Use capture-phase listeners
   * so deferred src is applied before theme.js switches the visible image.
   */
  function bindProductItemSwatchDeferredLoading() {
    if (document.documentElement.dataset.skProductItemSwatchDeferredBound === 'true') {
      return;
    }

    document.documentElement.dataset.skProductItemSwatchDeferredBound = 'true';

    document.addEventListener(
      'change',
      (event) => {
        const input = event.target.closest('.product-item-meta__swatch-list .color-swatch__radio');

        if (!input) {
          return;
        }

        void loadSwatchVariantImage(input);
      },
      true,
    );

    document.addEventListener(
      'mouseenter',
      (event) => {
        const label = event.target.closest?.('.product-item-meta__swatch-list .color-swatch__item');

        if (!label) {
          return;
        }

        const input = label.previousElementSibling;

        if (!input?.classList?.contains('color-swatch__radio')) {
          return;
        }

        void loadSwatchVariantImage(input);
      },
      true,
    );
  }

  bindProductItemSwatchDeferredLoading();
  bindProductItemSecondaryHover();

  document.addEventListener('shopify:section:load', (event) => {
    bindProductItemSecondaryHover(event.target);
  });

  window.SKProductItemDeferredImages = {
    ensure: ensureDeferredImageLoaded,
    loadSecondary: loadSecondaryHoverImage,
  };
})();

/**
 * Horizontal product-list scrollers hide off-screen items from the viewport, so
 * native lazy loading delays page-2+ images until prev/next scroll. Keep lazy/low
 * in markup but eagerly start fetching once the list is active and visible.
 *
 * Section index gates how aggressive preloading is:
 * - index <= 2: preload horizontally visible row + one scroller width ahead
 * - index == 3: same, but only after the list intersects with a tighter margin
 * - index > 3: rely on native lazy loading only
 */
(() => {
  if (window.SKProductListScrollerImages) {
    return;
  }

  function getSectionIndex(productList) {
    const value = productList.dataset.sectionIndex;

    if (!value) {
      return 0;
    }

    const parsed = parseInt(value, 10);

    return Number.isFinite(parsed) ? parsed : 0;
  }

  function getScrollerPreloadRootMargin(sectionIndex) {
    if (sectionIndex <= 2) {
      return '200px 0px';
    }

    if (sectionIndex === 3) {
      return '80px 0px';
    }

    return null;
  }

  function preloadScrollerImages(productList) {
    const sectionIndex = getSectionIndex(productList);

    if (sectionIndex > 3) {
      return;
    }

    const scroller = productList.querySelector('.product-list__inner--scroller');

    if (!scroller) {
      return;
    }

    const scrollerRect = scroller.getBoundingClientRect();
    const preloadMargin = scroller.clientWidth;

    scroller
      .querySelectorAll(
        'img.product-item__primary-image[loading="lazy"]:not([hidden]):not([data-deferred-src])',
      )
      .forEach((img) => {
        if (img.dataset.scrollerPreloaded === 'true') {
          return;
        }

        const imgRect = img.getBoundingClientRect();
        const inHorizontalRange =
          imgRect.left < scrollerRect.right + preloadMargin &&
          imgRect.right > scrollerRect.left;

        if (!inHorizontalRange) {
          return;
        }

        img.setAttribute('data-scroller-preloaded', 'true');
        img.loading = 'eager';
      });
  }

  function bindScrollerScrollPreload(productList) {
    const scroller = productList.querySelector('.product-list__inner--scroller');

    if (!scroller || scroller.dataset.skScrollPreloadBound === 'true') {
      return;
    }

    scroller.dataset.skScrollPreloadBound = 'true';

    let ticking = false;

    scroller.addEventListener(
      'scroll',
      () => {
        if (ticking) {
          return;
        }

        ticking = true;

        requestAnimationFrame(() => {
          preloadScrollerImages(productList);
          ticking = false;
        });
      },
      { passive: true },
    );
  }

  function observeProductList(productList) {
    if (productList.dataset.scrollerPreloadObserved === 'true') {
      return;
    }

    productList.dataset.scrollerPreloadObserved = 'true';

    if (!productList.querySelector('.product-list__inner--scroller')) {
      return;
    }

    const sectionIndex = getSectionIndex(productList);
    const rootMargin = getScrollerPreloadRootMargin(sectionIndex);

    if (!rootMargin) {
      return;
    }

    bindScrollerScrollPreload(productList);

    const tryPreload = () => {
      if (productList.hidden) {
        return;
      }

      preloadScrollerImages(productList);
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              tryPreload();
              observer.disconnect();
            }
          });
        },
        { rootMargin },
      );

      observer.observe(productList);
    } else {
      tryPreload();
    }
  }

  function initProductListScrollerPreload(root = document) {
    if (!root.querySelectorAll) {
      return;
    }

    root.querySelectorAll('product-list').forEach(observeProductList);
  }

  function patchProductListScrollerPreload() {
    const tagName = 'product-list';

    const applyPatch = (Constructor) => {
      if (!Constructor || Constructor.prototype.__skScrollerPreloadPatched) {
        return;
      }

      const originalAttributeChanged = Constructor.prototype.attributeChangedCallback;

      Constructor.prototype.attributeChangedCallback = function productListAttributeChangedWithPreload(
        name,
        oldValue,
        newValue,
      ) {
        if (originalAttributeChanged) {
          originalAttributeChanged.call(this, name, oldValue, newValue);
        }

        if (name === 'hidden' && !this.hidden) {
          preloadScrollerImages(this);
        }
      };

      Constructor.prototype.__skScrollerPreloadPatched = true;
    };

    const Constructor = customElements.get(tagName);

    if (Constructor) {
      applyPatch(Constructor);
    } else {
      customElements.whenDefined(tagName).then(() => applyPatch(customElements.get(tagName)));
    }
  }

  patchProductListScrollerPreload();
  initProductListScrollerPreload(document);

  document.addEventListener('shopify:section:load', (event) => {
    patchProductListScrollerPreload();
    initProductListScrollerPreload(event.target);
  });

  window.SKProductListScrollerImages = {
    preload: preloadScrollerImages,
    init: initProductListScrollerPreload,
  };
})();

/**
 * Load below-fold video carousel posters / avatars only when the item enters view.
 */
(() => {
  if (window.SKVideoCarouselDeferredImages) {
    return;
  }

  function loadDeferredImagesInItem(item) {
    if (!window.SKDeferredImages?.loadSingle) {
      return;
    }

    item.querySelectorAll('img[data-deferred-src]').forEach((img) => {
      window.SKDeferredImages.loadSingle(img);
    });
  }

  function observeVideoCarouselItem(item) {
    if (item.dataset.skVideoDeferredObserved === 'true') {
      return;
    }

    item.dataset.skVideoDeferredObserved = 'true';

    if (!item.querySelector('img[data-deferred-src]')) {
      return;
    }

    const carousel = item.closest('video-carousel');
    const sectionIndex = parseInt(carousel?.dataset.sectionIndex || '999', 10);
    const rootMargin = sectionIndex <= 2 ? '200px 0px' : '120px 0px';

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              loadDeferredImagesInItem(item);
              observer.disconnect();
            }
          });
        },
        { rootMargin, threshold: 0.01 },
      );

      observer.observe(item);
      return;
    }

    loadDeferredImagesInItem(item);
  }

  function initVideoCarouselDeferred(root = document) {
    if (!root.querySelectorAll) {
      return;
    }

    root.querySelectorAll('custom-video-carousel-item').forEach(observeVideoCarouselItem);
  }

  initVideoCarouselDeferred(document);

  document.addEventListener('shopify:section:load', (event) => {
    initVideoCarouselDeferred(event.target);
  });

  window.SKVideoCarouselDeferredImages = {
    init: initVideoCarouselDeferred,
    load: loadDeferredImagesInItem,
  };
})();

/**
 * 地区选择器重定向
 */
class CountryLocalizationForm extends HTMLElement {
  _getCountryDomains() {
    return window.themeVariables?.settings?.countryRedirectDomains || {};
  }

  connectedCallback() {
    this._onClick = this._onClick.bind(this);
    this.addEventListener('click', this._onClick, true);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick, true);
  }

  _getLocalCountryCode() {
    const currentHost = window.location.hostname;

    for (const [countryCode, domain] of Object.entries(this._getCountryDomains())) {
      const domainHost = domain.split('/')[0];

      if (currentHost === domainHost) {
        return countryCode;
      }
    }

    return null;
  }

  _shouldRedirect(countryCode, targetDomain) {
    if (!targetDomain) {
      return false;
    }

    const localCountryCode = this._getLocalCountryCode();

    // 区域站（如 US 站）：当前站点所属国家走 Shopify 默认逻辑，其他国家按配置外跳
    if (localCountryCode) {
      return countryCode !== localCountryCode;
    }

    // 主站：仅配置中的国家外跳，其余走 Shopify 默认逻辑
    return true;
  }

  _onClick(event) {
    const button = event.target.closest('button[name="country_code"]');
    if (!button || !this.contains(button)) return;

    const countryCode = String(button.value || '')
      .trim()
      .toUpperCase();

    if (!countryCode) return;

    const targetDomain = this._getCountryDomains()[countryCode];

    if (!this._shouldRedirect(countryCode, targetDomain)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    window.location.href = this._buildUrl(targetDomain);
  }

  _buildUrl(domain) {
    const target = domain.replace(/^https?:\/\//, '').replace(/\/+$/, '');
    const slashIndex = target.indexOf('/');

    const host = slashIndex === -1 ? target : target.slice(0, slashIndex);
    const marketPrefix =
      slashIndex === -1 ? '' : '/' + target.slice(slashIndex + 1).replace(/^\/+|\/+$/g, '');

    let cleanPath =
      window.location.pathname.replace(/^\/([a-z]{2}(?:-[a-z]{2})?)(?=\/|$)/i, '') || '/';

    if (!cleanPath.startsWith('/')) {
      cleanPath = '/' + cleanPath;
    }

    const shouldKeepPath =
      cleanPath !== '/' &&
      !cleanPath.startsWith('/cart') &&
      !cleanPath.startsWith('/checkout') &&
      !cleanPath.startsWith('/account');

    let targetUrl = 'https://' + host + marketPrefix;

    if (shouldKeepPath) {
      targetUrl += cleanPath;
    }

    targetUrl += window.location.search;

    return targetUrl;
  }
}

if (!window.customElements.get('country-localization-form')) {
  window.customElements.define('country-localization-form', CountryLocalizationForm);
}

class SkComplementaryCarousel extends HTMLElement {
  connectedCallback() {
    this.scroller = this.querySelector('.scroller');
    this.prevButton = this.querySelector('.prev-next-button--prev');
    this.nextButton = this.querySelector('.prev-next-button--next');

    if (!this.scroller || !this.prevButton || !this.nextButton) {
      return;
    }

    this._boundUpdate = this._updateButtons.bind(this);
    this._boundScrollPrev = () => this._scroll(-1);
    this._boundScrollNext = () => this._scroll(1);

    this.addEventListener('prev-next:prev', this._boundScrollPrev);
    this.addEventListener('prev-next:next', this._boundScrollNext);
    this.scroller.addEventListener('scroll', this._boundUpdate, { passive: true });
    window.addEventListener('resize', this._boundUpdate);

    if (window.ResizeObserver) {
      this._resizeObserver = new ResizeObserver(this._boundUpdate);
      this._resizeObserver.observe(this.scroller);
    }

    this.scroller.querySelectorAll('img').forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', this._boundUpdate, { once: true });
      }
    });

    requestAnimationFrame(this._boundUpdate);
  }

  disconnectedCallback() {
    if (!this.scroller) {
      return;
    }

    this.removeEventListener('prev-next:prev', this._boundScrollPrev);
    this.removeEventListener('prev-next:next', this._boundScrollNext);
    this.scroller.removeEventListener('scroll', this._boundUpdate);
    window.removeEventListener('resize', this._boundUpdate);
    this._resizeObserver?.disconnect();
  }

  _getDirectionFlip() {
    return window.themeVariables?.settings?.direction === 'ltr' ? 1 : -1;
  }

  _getScrollAmount() {
    const item = this.scroller.querySelector('.product-item');
    const list = this.scroller.querySelector('.product-content__featured-products-list');

    if (!item || !list) {
      return this.scroller.clientWidth;
    }

    const gap = parseFloat(getComputedStyle(list).columnGap || getComputedStyle(list).gap) || 0;
    return item.offsetWidth + gap;
  }

  _scroll(direction) {
    this.scroller.scrollBy({
      left: direction * this._getScrollAmount() * this._getDirectionFlip(),
      behavior: 'smooth',
    });
  }

  _updateButtons() {
    const flip = this._getDirectionFlip();
    const scrollLeft = this.scroller.scrollLeft * flip;
    const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;
    const tolerance = 2;
    const canScroll = maxScroll > tolerance;
    const canScrollPrev = canScroll && scrollLeft > tolerance;
    const canScrollNext = canScroll && scrollLeft < maxScroll - tolerance;

    this._setButtonState(this.prevButton, canScrollPrev);
    this._setButtonState(this.nextButton, canScrollNext);
  }

  _setButtonState(button, isVisible) {
    button.hidden = !isVisible;
    button.toggleAttribute('disabled', !isVisible);
  }
}

if (!window.customElements.get('sk-complementary-carousel')) {
  window.customElements.define('sk-complementary-carousel', SkComplementaryCarousel);
}
